import { create } from 'zustand';
import { useEffect } from 'react';
import { TOAST_DURATION_MS } from '@nudge/shared';

interface ToastEntry { id: string; text: string; tone: 'info' | 'success' | 'error'; }

interface ToastState {
  toasts: ToastEntry[];
  push: (text: string, tone?: ToastEntry['tone']) => void;
  remove: (id: string) => void;
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  push(text, tone = 'info') {
    const id = `t-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    set({ toasts: [...get().toasts, { id, text, tone }] });
    setTimeout(() => get().remove(id), TOAST_DURATION_MS);
  },
  remove(id) {
    set({ toasts: get().toasts.filter((t) => t.id !== id) });
  },
}));

export function toast(text: string, tone: ToastEntry['tone'] = 'info') {
  useToastStore.getState().push(text, tone);
}

export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);
  return (
    <div className="fixed inset-x-0 bottom-24 z-50 flex flex-col items-center gap-2 px-4 pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`fade-in pointer-events-auto rounded-xl px-4 py-2 text-sm shadow-lg backdrop-blur ${
            t.tone === 'error'
              ? 'bg-rose/90 text-white'
              : t.tone === 'success'
              ? 'bg-sage/90 text-white'
              : 'bg-surface text-text-primary border border-border-subtle'
          }`}
        >
          {t.text}
        </div>
      ))}
    </div>
  );
}

export function useEscClose(open: boolean, close: () => void) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);
}
